import React, { useRef, useState } from 'react';

import { Box, Grid } from '@chakra-ui/react';
import { TRACKING_QUERY_TYPES } from 'components/RabbitOKRSidePanel/PrimaryPanel/Sections/Measure/AutomaticTracking/AutomaticTrackingMetrics';
import { useLinkPluginMetricMutation } from 'components/RabbitOKRSidePanel/PrimaryPanel/Sections/Measure/AutomaticTracking/datahooks';
import { useSelector } from 'react-redux';
import { kRDrawerSelector } from 'selectors/kRDrawer';
import { kRDrawerSecondaryPanelSelector } from 'selectors/krSecondaryPanel';

import InputLabel from 'uiToolkitV1/InputLabel';
import { InputV2 } from 'uiToolkitV1/InputV2';

import {
  CustomNumeratorComp,
  CustomDenominatorComp,
  QueryStringComp,
} from '../../PrimaryPanel/Sections/Measure/AutomaticTracking/TextInputComponents';
import { PluginPanelFooter } from './PluginPanelFooter';

const useCustomMetricValidation = ({ name, query, ratioQuery, isCustomPercentage = false }) => {
  if (name?.length === 0) {
    return 'Name cannot be empty';
  }
  if (query?.length === 0) {
    return `${isCustomPercentage ? 'Numerator query' : 'Query string'} cannot be empty`;
  }
  if (isCustomPercentage && ratioQuery?.length === 0) {
    return `Denominator query cannot be empty`;
  }

  return '';
};

export const PluginPanelContent = () => {
  const { uuid, trackedByPlugin } = useSelector(kRDrawerSelector);

  const { details } = useSelector(kRDrawerSecondaryPanelSelector);

  const linkCustomMetricMutation = useLinkPluginMetricMutation({
    okrId: uuid,
    pluginType: trackedByPlugin,
  });

  const { queryType, isPredefined, query } = details;

  const nameRef = useRef(null);

  const [localMetricName, setLocalMetricName] = useState('');
  const [numeratorQuery, setNumeratorQuery] = useState(isPredefined ? query : '');
  const [denominatorQuery, setDenominatorQuery] = useState('');

  const validationError = useCustomMetricValidation({
    name: localMetricName,
    query: numeratorQuery,
    ratioQuery: denominatorQuery,
    isCustomPercentage: queryType === TRACKING_QUERY_TYPES.CUSTOM_PERCENTAGE,
  });

  const isModified = localMetricName.length > 0 || numeratorQuery.length > 0 || denominatorQuery.length > 0;

  const handleSave = () => {
    if (isPredefined) {
      linkCustomMetricMutation.mutate({
        isCustomQuery: true,
        name: localMetricName,
        query: numeratorQuery,
        queryType: TRACKING_QUERY_TYPES.CUSTOM,
      });
      return;
    }

    if (queryType === TRACKING_QUERY_TYPES.CUSTOM) {
      linkCustomMetricMutation.mutate({
        isCustomQuery: true,
        name: localMetricName,
        query: numeratorQuery,
        queryType,
      });
      return;
    }

    if (queryType === TRACKING_QUERY_TYPES.CUSTOM_PERCENTAGE) {
      linkCustomMetricMutation.mutate({
        isCustomQuery: true,
        name: localMetricName,
        query: numeratorQuery,
        ratioQuery: denominatorQuery,
        queryType,
      });
    }
  };

  const handleLocalMetricNameChange = (ev) => {
    const metricName = ev?.target?.value;

    setLocalMetricName(metricName);

    //  const errorMessage = validateAndReturnErrorMessage(okrName);

    //  setError(errorMessage);
  };

  const handleNumeratorQueryChange = (ev) => {
    const numeratorQuery = ev?.target?.value;

    setNumeratorQuery(numeratorQuery);

    //  const errorMessage = validateAndReturnErrorMessage(okrName);

    //  setError(errorMessage);
  };

  const handleDenominatorQueryChange = (ev) => {
    const denominatorQuery = ev?.target?.value;

    setDenominatorQuery(denominatorQuery);

    //  const errorMessage = validateAndReturnErrorMessage(okrName);

    //  setError(errorMessage);
  };

  const onKeyDown = (ev) => {
    if (ev.key === 'Enter') {
      nameRef?.current?.click();
    }
  };

  return (
    <Box height="calc(100% - 41px)">
      {/* Invisible Box to blur */}
      <Box id="invisible-box" ref={nameRef} display="none" />
      <Box px={2} py={3} height="calc(100% - 72px)" overflowY="auto">
        <Box>
          <InputLabel
            labelText="Metric Name"
            containerProps={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              marginBottom: '0.5rem',
            }}
          >
            <InputV2
              placeholder="Please enter a meaningful Metric Name"
              isDashed
              value={localMetricName}
              onChange={handleLocalMetricNameChange}
              onKeyDown={onKeyDown}
            />
          </InputLabel>
        </Box>
        {queryType === TRACKING_QUERY_TYPES.CUSTOM || isPredefined ? (
          <CustomMetricSection queryString={numeratorQuery} onChange={handleNumeratorQueryChange} />
        ) : null}
        {queryType === TRACKING_QUERY_TYPES.CUSTOM_PERCENTAGE ? (
          <CustomPercentageMetricSection
            numeratorQuery={numeratorQuery}
            handleNumeratorQueryChange={handleNumeratorQueryChange}
            denominatorQuery={denominatorQuery}
            handleDenominatorQueryChange={handleDenominatorQueryChange}
          />
        ) : null}
      </Box>
      <Box flex={0}>
        <PluginPanelFooter
          onSave={handleSave}
          isModified={isModified}
          isDisabled={validationError.length > 0}
          disabledReason={validationError}
        />
      </Box>
    </Box>
  );
};

const CustomMetricSection = ({ queryString, onChange }) => (
  <Box>
    <QueryStringComp queryString={queryString} onChange={onChange} />
  </Box>
);

const CustomPercentageMetricSection = ({
  numeratorQuery,
  handleNumeratorQueryChange,
  denominatorQuery,
  handleDenominatorQueryChange,
}) => (
  <Grid gridGap={2}>
    <CustomNumeratorComp onChange={handleNumeratorQueryChange} queryString={numeratorQuery} />
    <CustomDenominatorComp onChange={handleDenominatorQueryChange} queryString={denominatorQuery} />
    <QueryStringComp queryString={`${numeratorQuery}/${denominatorQuery}`} isReadOnly isDisabled />
  </Grid>
);
